// src/pages/Sensor/EditSensor.tsx

import {
  DeleteTwoTone,
  DoNotDisturbTwoTone,
  ImportExportTwoTone,
  MoreVertTwoTone,
  PowerOffTwoTone,
  PowerTwoTone,
  SaveTwoTone,
  SettingsTwoTone,
  MemoryTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import Chart from 'react-google-charts'
import { useNavigate, useParams } from 'react-router-dom'
import JsonView from 'react18-json-view'

import {
  SensorRecord,
  SensorId,
  CounterReadingDate,
  CounterReading,
  SensorModel,
  DataSamplingDate,
  CorrectionValue,
  SamplingMode,
  ValueMode,
} from '@/types/domain'
import { ContractId, ContractPool } from '@/types/domain/contract'
import { FolderPool } from '@/types/domain/folder'
import { PayloadPool } from '@/types/domain/payload'
import { UserPool } from '@/types/domain/user'

import {
  categoryByType,
  getRelativeDate,
  getStorage,
  sanitizeJSON,
  SensorIcon,
  sensorType as SensorType,
  setStorage,
  SORT_ATTR_NAME,
  SORT_NAME,
} from '../../api'
import {
  AM,
  AMItem,
  DlgSensorUserAssignments,
  DlgSensorIndoorOutdoorAssignments,
  HStack,
  KPage,
  KSelect,
  KTextField,
  Section,
  DlgDelete,
} from '../../components'
import {
  useContractPool,
  useFolderInstallations,
  useFolderPool,
  useFolderSensors,
  usePayloadPool,
  useSensor,
  useSensorApi,
  useTimeSeriesApi,
  useUserApi,
  useUserPool,
} from '../../hooks'

import { CustomFields, Benchmarks, SensorFactors } from './components'

import 'react18-json-view/src/style.css'
// If dark mode is needed, import `dark.css`.
// import 'react18-json-view/src/dark.css'

const germanNotation = value => value && value.toString().replace(',', '.')

// Merge attributes with default min max values
const mergeMinMaxAttributes = (defaultAttrs, attr) => {
  const jsonMap = Object.fromEntries(
    attr.map(attr => [attr.attribute_code, attr.value])
  )
  return defaultAttrs.map(attr => ({
    ...attr,
    value: jsonMap[attr.attribute_code] || '',
  }))
}

const NOT_SPECIFIED = 'Keine Angabe'

type EditSensorProps = {
  PAGE_CONFIG?: string
}

export const EditSensor = ({
  PAGE_CONFIG = 'settings_sensor_edit',
}: EditSensorProps) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const sId: SensorId = Number(id)

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))

  const cPool: ContractPool = useContractPool()
  const fPool: FolderPool = useFolderPool()
  const pPool: PayloadPool = usePayloadPool()
  const uPool: UserPool = useUserPool()

  const { setUsersBySensorId } = useUserApi()

  const {
    remove: removeSensor,
    save: saveSensor,
    update: updateSensor,
  } = useSensorApi()

  // default sensor object shape
  const [sensor, setSensor] = useState({
    attributes_values: null,
    battery_comment: '',
    battery_date: '',
    battery_type: '',
    counter_reading: 0,
    counter_reading_date: '',
    data_sampling_date: '',
    folder_id: null,
    installation: null,
    is_active: true,
    meter_number: '',
    meter_unit: '',
    name: '',
    previous_lifecycle_status: 0,
    lifecycle_status: 0,
    parent_id: '',
    sampling_mode: '',
    sensor_model: '',
    sensor_target: '',
    sensor_type: null,
    sub_sensor_target: 0,
    subcategory: 0,
    type_in_folder: '',
    unit: '',
  }) as any

  const [sensorPayload, setSensorPayload] = useState({})
  const [contractId, setContractId] = useState<ContractId>(null)
  const [installation, setInstallation] = useState()

  const [actionMenu, setActionMenu] = useState(null)
  const [showAssignment, setShowAssignment] = useState(false)
  const [showSensorAssignment, setShowSensorAssignment] = useState(false)

  const [attributes, setAttributes] = useState<any[]>([])
  const [factorPeriodList, setFactorPeriodList] = useState([])

  const [sensorUsers, setSensorUsers] = useState([])
  const [outdoorSensor, setOutdoorSensor] = useState([])
  const [indoorSensor, setIndoorSensor] = useState([])
  const [correctionType, setCorrectionType] = useState(0)
  const [selectedSensorType, setSelectedSensorType] = useState<
    'indoor' | 'outdoor'
  >('outdoor')
  const [initialLifecycleStatus, setInitialLifecycleStatus] = useState<
    number | null
  >(null)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const [rangeMin, setRangeMin] = useState()
  const [rangeMax, setRangeMax] = useState()

  const defaultMinMaxAttributes = [
    {
      attribute_code: 'daily_upward_deviation_workday',
      label: 'maximaler Verbrauchsanstieg an Werktagen',
    },
    {
      attribute_code: 'daily_downward_deviation_workday',
      label: 'maximaler Verbrauchsrückgang an Werktagen',
    },
    {
      attribute_code: 'daily_upward_deviation_weekend',
      label: 'maximaler Verbrauchsanstieg an Wochenenden',
    },
    {
      attribute_code: 'daily_downward_deviation_weekend',
      label: 'maximaler Verbrauchsrückgang an Wochenenden',
    },
    {
      attribute_code: 'monthly_downward_deviation',
      label: 'maximaler monatlicher Verbrauchsrückgang',
    },
    {
      attribute_code: 'monthly_upward_deviation',
      label: 'maximaler monatlicher Verbrauchsanstieg',
    },
    {
      attribute_code: 'quarterly_downward_deviation',
      label: 'maximaler quartals Verbrauchsrückgang',
    },
    {
      attribute_code: 'quarterly_upward_deviation',
      label: 'maximaler quartals Verbrauchsanstieg',
    },
    {
      attribute_code: 'yearly_downward_deviation',
      label: 'maximaler jährlicher Verbrauchsrückgang',
    },
    {
      attribute_code: 'yearly_upward_deviation',
      label: 'maximaler jährlicher Verbrauchsanstieg',
    },
  ]

  const attributesList = mergeMinMaxAttributes(
    defaultMinMaxAttributes,
    attributes
  )

  const sensorData = useSensor(sId) as any

  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  /* attributes */
  useEffect(() => {
    for (const attr of attributes) {
      const { attribute_code, value } = attr

      if (attribute_code === 'range_min') setRangeMin(value)
      if (attribute_code === 'range_max') setRangeMax(value)
    }
  }, [attributes])

  const fSensors: SensorRecord[] = useFolderSensors(sensorData?.folder_id)
  const fInstallations = useFolderInstallations(sensorData?.folder_id) as any

  /* sensorData */
  useEffect(() => {
    if (!sensorData) return
    const {
      contracts,
      periods,
      outdoor_sensor,
      indoor_sensor,
      correction_type,
      users,
    } = sensorData

    const {
      attributes_values,
      device_id,
      installation,
      is_active,
      lifecycle_status,
    } = sensorData
    setInitialLifecycleStatus(lifecycle_status)
    setSensor(prev => ({
      ...prev,
      ...sensorData,
      is_active: is_active === 1,
    }))

    if (installation[0]) setInstallation(installation[0].entity_id)

    if (contracts)
      setContractId(contracts.map(contract => contract.entity_id)[0])

    if (periods) {
      setFactorPeriodList(
        periods.map(period => ({
          period_start: period.period.period_start,
          period_end: period.period.period_end,
          sensor_id: period.period.sensor_id,
          factor: period.period.factor,
        }))
      )
    }

    setAttributes(
      attributes_values.map(item => {
        const { attribute_code, label } = item
        let { value } = item
        if (attribute_code && value) value = value.replace(',', '.')

        return { attribute_code, value, label }
      })
    )

    if (users) setSensorUsers(users)
    if (outdoor_sensor) setOutdoorSensor(outdoor_sensor)
    if (indoor_sensor) setIndoorSensor(indoor_sensor)
    if (correction_type) setCorrectionType(correction_type)

    if (pPool) {
      const hasPayload = pPool.find(f => f.device_id === device_id)

      if (hasPayload) setSensorPayload(sanitizeJSON(hasPayload.payload))
    }
  }, [pPool, sensorData])

  const TZO = new Date().getTimezoneOffset() * 60 * 1000
  const _1H = 60 * 60 * 1000
  const _24H = 24 * _1H
  const _NOW = new Date(Date.now()).getTime()
  const _YESTERDAY = _NOW - _24H

  const from = useMemo(() => {
    const date = new Date(_YESTERDAY)
    date.setTime(date.getTime() - TZO)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }, [_YESTERDAY, TZO])

  const to = useMemo(() => {
    const date = new Date(_NOW)
    date.setTime(date.getTime() - TZO)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }, [_NOW, TZO])

  const { getTimeSeries } = useTimeSeriesApi()

  const [timeSeries, setTimeSeries] = useState([])

  useEffect(() => {
    const fetchTimeSeries = async () => {
      const data = await getTimeSeries(sId, from, to)

      setTimeSeries(data)
    }

    fetchTimeSeries()
  }, [getTimeSeries, from, to, sId])

  const onSubmit = async action => {
    const lifecycleStatusChanged =
      initialLifecycleStatus !== null &&
      Number(sensor.lifecycle_status) !== Number(initialLifecycleStatus)

    const record = {
      sensorId: sId,
      alert_after_months: sensor.alert_after_months,
      area_in_sqm: sensor.area_in_sqm,
      attributes,
      battery_comment: sensor.battery_comment,
      battery_date: sensor.battery_date,
      battery_type: sensor.battery_type,
      co2_factor: sensor.co2_factor,
      contract_id: [contractId],
      converter_factor: sensor.converter_factor,
      correction_value: sensor.correction_value,
      counter_reading: sensor.counter_reading,
      counter_reading_date: sensor.counter_reading_date,
      custom_fields: sensor.custom_fields,
      data_sampling_date: sensor.data_sampling_date,
      device_id: sensor.device_id,
      factor: sensor.factor,
      folder_id: sensor.folder_id,
      fuel_value: sensor.fuel_value,
      installation,
      is_active: sensor.is_active,
      meter_number: sensor.meter_number,
      meter_unit: sensor.meter_unit,
      name: sensor.name,
      number_of_employees: sensor.number_of_employees,
      outdoor_sensor: outdoorSensor,
      indoor_sensor: indoorSensor,
      correction_type: correctionType,
      lifecycle_status: sensor.lifecycle_status,
      previous_lifecycle_status: lifecycleStatusChanged
        ? initialLifecycleStatus
        : sensor.lifecycle_status,
      parent_id: sensor.parent_id,
      provider: sensor.provider,
      push_enabled: sensor.push_enabled,
      sampling_mode: sensor.sampling_mode,
      sensor_model: sensor.sensor_model,
      sensor_target: sensor.sensor_target,
      sensor_type: sensor.sensor_type,
      state_number: sensor.state_number,
      sub_sensor_target: sensor.sub_sensor_target,
      subcategory: sensor.subcategory,
      successor_id: sensor.successor_id,
      time_periods: factorPeriodList,
      type_in_folder: sensor.type_in_folder,
      unit: sensor.unit,
      value_mode: sensor.value_mode,
      warning_after_months: sensor.warning_after_months,
    }

    switch (action) {
      case 'save':
        await saveSensor(record as any)
        setUsersBySensorId(sId, sensorUsers)
        break

      case 'update':
        await updateSensor(record as any)
        setUsersBySensorId(sId, sensorUsers)
        break

      case 'delete':
        await removeSensor(sId)
        navigate('/sensors')
        break

      default:
        break
    }
  }

  const handleChangeAttr = (event, name) => {
    const { value } = event.target
    const isMinMaxAttribute = defaultMinMaxAttributes.some(
      attr => attr.attribute_code === name
    )
    const newValue =
      isMinMaxAttribute && value === '' ? null : value.replace(',', '.')
    const attributeExists = attributes.some(
      item => item.attribute_code === name
    )
    const updatedAttributes = attributeExists
      ? attributes.map(item =>
          item.attribute_code === name ? { ...item, value: newValue } : item
        )
      : [
          ...attributes,
          {
            attribute_code: name,
            value: newValue,
          },
        ]
    setAttributes(updatedAttributes)
  }

  const [series, setSeries] = useState([])

  useEffect(() => {
    if (!timeSeries) return
    const header = [{ type: 'datetime', label: 'Time' }, sensor?.unit]
    const rows = timeSeries?.map(item => [
      new Date(item.datetime),
      Number(item.value),
    ])

    setSeries([header, ...rows])
  }, [timeSeries, sensor?.unit])

  const isConsumptionSensor =
    categoryByType(
      sensor.sensor_type,
      sensor.sensor_target,
      sensor.sub_sensor_target
    ) === 'performance'

  const onChangeLabel = event => {
    const name = event.target.value

    setSensor(prev => ({ ...prev, name }))
  }

  const onChangeProvider = event => {
    const provider = event.target.value

    setSensor(prev => ({ ...prev, provider }))
  }

  const onChangeDeviceId = event => {
    const device_id = event.target.value

    setSensor(prev => ({ ...prev, device_id }))
  }

  const onChangeSensorType = event => {
    const sensor_type = event.target.value

    setSensor(prev => ({ ...prev, sensor_type }))
  }

  const onChangeMeterNumber = event => {
    const meter_number = event.target.value

    setSensor(prev => ({ ...prev, meter_number }))
  }

  const onChangeSubCategory = event => {
    const subcategory = event.target.value

    setSensor(prev => ({ ...prev, subcategory }))
  }

  const onChangeTarget = event => {
    const sensor_target = event.target.value

    if (sensor_target !== null) setSensor(prev => ({ ...prev, sensor_target }))
  }

  const onChangeSubSensorTarget = event => {
    const sub_sensor_target = event.target.value

    setSensor(prev => ({ ...prev, sub_sensor_target }))
  }

  const onChangeLifecycleStatus = event => {
    const lifecycle_status = event.target.value

    setSensor(prev => ({
      ...prev,
      lifecycle_status,
    }))
  }

  const pushEnabledKnown = typeof sensor.push_enabled === 'boolean'

  const onChangePushEnabled = event => {
    const push_enabled: boolean = event.target.checked

    setSensor(prev => ({ ...prev, push_enabled }))
  }

  const onChangeFolder = event => {
    const folder_id = event.target.value

    setSensor(prev => ({ ...prev, folder_id }))
  }

  const onChangeParentId = event => {
    const parent_id: SensorId = event.target.value

    setSensor(prev => ({ ...prev, parent_id }))
  }

  const onChangeSuccesorId = event => {
    const successor_id: SensorId = event.target.value

    setSensor(prev => ({ ...prev, successor_id }))
  }

  const onChangeTypeInFolder = event => {
    const type_in_folder = event.target.value

    setSensor(prev => ({ ...prev, type_in_folder }))
  }

  const onChangeUnit = event => {
    const unit = event.target.value

    setSensor(prev => ({ ...prev, unit }))
  }

  const onOpenAM = event => setActionMenu(event.currentTarget)
  const onChangeTab = (event, tab) => setConfig(prev => ({ ...prev, tab }))

  const onChangeContractId = event => setContractId(event.target.value)
  const onChangeInstallation = event => setInstallation(event.target.value)

  const onChangeSamplingMode = event => {
    const sampling_mode: SamplingMode = event.target.value

    setSensor(prev => ({ ...prev, sampling_mode }))
  }

  const onChangeCounterReading = event => {
    const counter_reading: CounterReading = event.target.value

    setSensor(prev => ({ ...prev, counter_reading }))
  }

  const onChangeMeterUnit = event => {
    const meter_unit = event.target.value

    setSensor(prev => ({ ...prev, meter_unit }))
  }

  const onChangeCounterReadingDate = event => {
    const counter_reading_date: CounterReadingDate = event.target.value

    setSensor(prev => ({ ...prev, counter_reading_date }))
  }

  const onChangeBatteryComment = event => {
    const battery_comment = event.target.value

    setSensor(prev => ({ ...prev, battery_comment }))
  }

  const onChangeBatteryDate = event => {
    const battery_date = event.target.value

    setSensor(prev => ({ ...prev, battery_date }))
  }

  const onChangeBatteryType = event => {
    const battery_type = event.target.value

    setSensor(prev => ({ ...prev, battery_type }))
  }

  const onChangeBatteryWarning = event => {
    const warning_after_months = event.target.value

    setSensor(prev => ({ ...prev, warning_after_months }))
  }

  const onChangeValueMode = event => {
    const value_mode: ValueMode = event.target.value

    setSensor(prev => ({ ...prev, value_mode }))
  }

  const onChangeBatteryAlert = event => {
    const alert_after_months = event.target.value

    setSensor(prev => ({ ...prev, alert_after_months }))
  }

  const onChangeCorrectionValue = event => {
    const correction_value: CorrectionValue = event.target.value

    setSensor(prev => ({ ...prev, correction_value }))
  }

  const onChangeDataSamplingDate = event => {
    const data_sampling_date: DataSamplingDate = event.target.value

    setSensor(prev => ({ ...prev, data_sampling_date }))
  }

  const onChangeSensorModel = event => {
    const sensor_model: SensorModel = event.target.value

    setSensor(prev => ({ ...prev, sensor_model }))
  }

  const onChangeCorrectionType = event => {
    const correction_type = event.target.value

    setCorrectionType(correction_type)
  }

  return (
    <>
      <AM
        anchorEl={actionMenu}
        caption={'Sensoren'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'sensors'}
      >
        {[
          {
            title: 'Importieren',
            icon: <ImportExportTwoTone />,
            fn: () => navigate(`/sensors/${sId}/import`),
          },
          {
            title: 'Aktivieren / Deaktivieren',
            icon: sensor.is_active ? <PowerTwoTone /> : <PowerOffTwoTone />,
            fn: () =>
              setSensor(prev => ({ ...prev, is_active: !prev.is_active })),
          },
          {
            title: 'Speichern',
            icon: <SaveTwoTone />,
            fn: () => onSubmit('update'),
          },
          {
            title: 'Löschen',
            icon: <DeleteTwoTone />,
            fn: () => setIsDialogOpen(true),
          },
        ].map(item => (
          <AMItem
            caption={item.title}
            icon={item.icon}
            key={item.title}
            onClick={item.fn}
          />
        ))}
      </AM>

      <DlgDelete
        onCancel={() => setIsDialogOpen(false)}
        onConfirm={() => onSubmit('delete')}
        open={isDialogOpen}
      >
        <Typography>Löschen bestätigen</Typography>
      </DlgDelete>

      <DlgSensorUserAssignments
        data={{ id: sId, userIds: sensorUsers }}
        onChange={users => setSensorUsers(users)}
        onClose={() => setShowAssignment(false)}
        open={showAssignment}
      />

      <DlgSensorIndoorOutdoorAssignments
        data={{
          id: sId,
          fSensors,
          indoorSensor,
          outdoorSensor,
          selectedSensorType,
        }}
        onClose={() => setShowSensorAssignment(false)}
        onChangeIndoor={s => setIndoorSensor(s)}
        onChangeOutdoor={s => setOutdoorSensor(s)}
        open={showSensorAssignment}
      />

      <KPage
        action={
          <IconButton onClick={onOpenAM}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <Box
              alt={''}
              component={'img'}
              src={
                sensorData &&
                SensorIcon(sensor.sensor_type, sensorData.status || 'neutral')
              }
            />
          </Avatar>
        }
        title={`ID: ${sId}`}
      >
        <Tabs
          centered
          indicatorColor={'primary'}
          onChange={onChangeTab}
          sx={{ mb: 5 }}
          textColor={'primary'}
          value={config.tab}
        >
          <Tab label={'Einstellungen'} />
          <Tab label={'Payload'} />
          <Tab label={'Aktivität'} />
          <Tab disabled={!isConsumptionSensor} label={'Faktoren'} />
          <Tab disabled={!isConsumptionSensor} label={'Benchmark'} />
          <Tab disabled={!isConsumptionSensor} label={'Verbrauchsänderung'} />
          <Tab disabled={!isConsumptionSensor} label={'Custom Fields'} />
        </Tabs>
        {/* Einstellungen */}
        <Collapse in={config.tab === 0}>
          <Section title={'Allgemein'}>
            <KTextField
              disabled={!sensor.is_active}
              label={'Name (name)'}
              onChange={onChangeLabel}
              value={sensor.name || ''}
            />

            <KTextField
              disabled={true}
              label={'Provider (provider)'}
              onChange={onChangeProvider}
              value={sensor.provider || ''}
            />

            <KTextField
              disabled={!sensor.is_active}
              label={'Device ID (device_id)'}
              onChange={onChangeDeviceId}
              value={sensor.device_id || ''}
            />

            <KTextField
              disabled={true}
              label={'Sensor Typ (sensor_type)'}
              onChange={onChangeSensorType}
              readOnly
              value={sensor.sensor_type || ''}
            />

            <HStack>
              <KSelect
                disabled={!sensor.is_active}
                label={'Energietyp (type_in_folder)'}
                onChange={onChangeTypeInFolder}
                value={sensor.type_in_folder || ''}
              >
                {[
                  { label: NOT_SPECIFIED, value: '' },
                  { label: 'Strom', value: 'energy' },
                  { label: 'Strom Detail', value: 'energy_detail' },
                  { label: 'Gas', value: 'gas' },
                  { label: 'Gas Detail', value: 'gas_detail' },
                  { label: 'Wasser', value: 'water' },
                  { label: 'Wasser Detail', value: 'water_detail' },
                ].map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </KSelect>

              <KSelect
                disabled={!sensor.is_active}
                label={'Einheit (unit)'}
                onChange={onChangeUnit}
                value={sensor.unit || ''}
              >
                {[
                  { label: NOT_SPECIFIED, value: '' },
                  { label: 'kWh', value: 'kWh' },
                  { label: 'm³', value: 'm³' },
                  { label: '°C', value: '°C' },
                  { label: '%', value: '%' },
                  { label: 'Lux', value: 'Lux' },
                  { label: 'bar', value: 'bar' },
                  { label: 'ppm ', value: 'ppm' },
                ].map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </KSelect>
            </HStack>
          </Section>

          <Section title={'Daten'}>
            <KTextField
              disabled={!sensor.is_active}
              label={'Beginn der Datenerfassung (data_sampling_date)'}
              onChange={onChangeDataSamplingDate}
              type={'date'}
              value={sensor.data_sampling_date || ''}
            />

            <KTextField
              disabled={!sensor.is_active}
              label={'Sensor Modell (sensor_model)'}
              onChange={onChangeSensorModel}
              value={sensor.sensor_model || ''}
            />

            <KSelect
              disabled={!sensor.is_active}
              label={'Messverfahren (sampling_mode)'}
              onChange={onChangeSamplingMode}
              value={sensor.sampling_mode || ''}
            >
              {[
                { label: NOT_SPECIFIED, value: null },
                { label: 'Zählerstand', value: 1 },
                { label: 'Verbrauch', value: 2 },
                { label: 'Import', value: 3 },
              ].map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </KSelect>

            <KSelect
              disabled={!sensor.is_active}
              label={'Berechnungsverfahren (value_mode)'}
              onChange={onChangeValueMode}
              value={sensor.value_mode || ''}
            >
              {[
                { label: NOT_SPECIFIED, value: null },
                { label: 'diff', value: 'diff' },
                { label: 'sum', value: 'sum' },
              ].map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </KSelect>

            <KTextField
              disabled={!sensor.is_active}
              label={'Korrekturwert (correction_value)'}
              onChange={onChangeCorrectionValue}
              value={germanNotation(sensor.correction_value) || ''}
            />
          </Section>

          <Section title={'Target'}>
            <Stack>
              <ToggleButtonGroup
                disabled={!sensor.is_active}
                exclusive
                onChange={onChangeTarget}
                sx={{ m: 1, height: 55 }}
                value={sensor.sensor_target}
              >
                <ToggleButton value={''}>
                  <DoNotDisturbTwoTone sx={{ mr: 1 }} />
                  Keine Angabe
                </ToggleButton>

                <ToggleButton value={'performance'}>
                  <Box
                    component={'img'}
                    src={SensorIcon('performance')}
                    sx={{ mr: 1, width: 22 }}
                  />
                  Verbrauch
                </ToggleButton>

                <ToggleButton value={'feeling'}>
                  <Box
                    component={'img'}
                    src={SensorIcon('feeling')}
                    sx={{ mr: 1, width: 22 }}
                  />
                  Wohlbefinden
                </ToggleButton>

                <ToggleButton value={'security'}>
                  <Box
                    component={'img'}
                    src={SensorIcon('security')}
                    sx={{ mr: 1, width: 22 }}
                  />
                  Sicherheit
                </ToggleButton>
              </ToggleButtonGroup>

              <KSelect
                disabled={!sensor.is_active}
                label={'Sub Target (sub_sensor_target)'}
                onChange={onChangeSubSensorTarget}
                value={sensor.sub_sensor_target || ''}
              >
                {[
                  { label: NOT_SPECIFIED, value: null },
                  { label: 'Strom', value: 'energy' },
                  { label: 'Strom Detail', value: 'energy_detail' },
                  { label: 'Gas', value: 'gas' },
                  { label: 'Gas Detail', value: 'gas_detail' },
                  { label: 'Wasser', value: 'water' },
                  { label: 'Wasser Detail', value: 'water_detail' },
                  { label: 'Temperatur', value: 'temperature' },
                  { label: 'Luftqualität', value: 'co2' },
                  { label: 'Luftfeuchtigkeit', value: 'humidity' },
                  { label: 'Lichtstärke', value: 'light' },
                  { label: 'Wassermelder', value: 'waterDetected' },
                  {
                    label: 'Schließanlage (Schloss)',
                    value: 'tor1CurrentState',
                  },
                  { label: 'Schließanlage (Tür)', value: 'door' },
                  { label: 'Technische Anlagen', value: 'lifting' },
                  { label: 'Aufzug', value: 'elevator' },
                  { label: 'Anlagendruck', value: 'pressure' },
                  { label: 'Anlagentemperatur', value: 'heating' },
                  { label: 'Rauchmelder', value: 'smokeDetektor' },
                ].map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </KSelect>
            </Stack>

            <KSelect
              disabled={!sensor.is_active}
              label={'Kategorie (subcategory)'}
              onChange={onChangeSubCategory}
              value={sensor.subcategory || ''}
            >
              {[
                { label: NOT_SPECIFIED, value: null },
                { label: 'Allgemein', value: 'general' },
                { label: 'Beleuchtung', value: 'lighting' },
                { label: 'Anlage', value: 'machine' },
                { label: 'BT A', value: 'bta' },
                { label: 'BT B1', value: 'btb1' },
                { label: 'BT B2', value: 'btb2' },
                { label: 'Sonstige', value: 'other' },
              ].map(category => (
                <MenuItem key={category.label} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </KSelect>
            <KSelect
              disabled={!sensor.is_active}
              label={'Status'}
              onChange={onChangeLifecycleStatus}
              value={sensor.lifecycle_status ?? ''}
            >
              {[
                { label: 'Setup', value: 0 },
                { label: 'Aktiv', value: 1 },
                { label: 'Historisch', value: 2 },
                { label: 'Archiviert', value: 3 },
                { label: 'Irrelavant', value: 4 },
              ].map(status => (
                <MenuItem key={status.label} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </KSelect>

            {/* KAR-2542: push per sensor; e-mail alerts are not affected */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={sensor.push_enabled !== false}
                    onChange={onChangePushEnabled}
                  />
                }
                disabled={!sensor.is_active || !pushEnabledKnown}
                label={'Push-Benachrichtigungen (push_enabled)'}
              />
              <FormHelperText>
                {pushEnabledKnown
                  ? 'Aus: keine Pushes für diesen Sensor. E-Mail-Alerts, Status und Historie laufen weiter.'
                  : 'Wird vom Backend noch nicht geliefert.'}
              </FormHelperText>
            </Box>
          </Section>

          <Section title={'Zuordnungen'}>
            <HStack>
              <KSelect
                disabled={!sensor.is_active}
                label={`Gebäude ${
                  sensor.folder_id ? ` (ID:${sensor.folder_id})` : ' wählen ...'
                }`}
                onChange={onChangeFolder}
                value={sensor.folder_id || ''}
              >
                <MenuItem disabled={!sensor.folder_id} value={null}>
                  Keine Angabe
                </MenuItem>

                {fPool.sort(SORT_ATTR_NAME).map(f => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.attributes.name}
                  </MenuItem>
                ))}
              </KSelect>

              <KSelect
                disabled={true}
                label={`Vertrag (ID:${contractId})`}
                onChange={onChangeContractId}
                value={contractId || ''}
              >
                <MenuItem disabled={!contractId} value={null}>
                  Keine Angabe
                </MenuItem>

                {cPool.sort(SORT_ATTR_NAME).map(contract => (
                  <MenuItem key={contract.id} value={contract.id}>
                    {contract.attributes.name}
                  </MenuItem>
                ))}
              </KSelect>
            </HStack>

            <KSelect
              disabled={!sensor.is_active}
              label={'Anlage'}
              onChange={onChangeInstallation}
              value={installation || ''}
            >
              <MenuItem disabled={!installation} value={0}>
                Anlage wählen ...
              </MenuItem>

              {fInstallations.sort(SORT_NAME).map(item => (
                <MenuItem key={item.entity_id} value={item.entity_id}>
                  {item.name}
                </MenuItem>
              ))}
            </KSelect>

            <KSelect
              disabled={!sensor.is_active}
              error={
                sensor.parent_id &&
                !fSensors.find(f => Number(f.entity_id) === sensor.parent_id)
              }
              label={`Übergeordneter Sensor (ID:
                        ${sensor.parent_id})`}
              onChange={onChangeParentId}
              value={sensor.parent_id || ''}
            >
              <MenuItem disabled={!sensor.parent_id} value={null}>
                Übergeordneten Sensor wählen ...
              </MenuItem>

              {fSensors
                .filter(f => f.entity_id !== sensor.entity_id)
                .sort((a, b) => (a.device_id > b.device_id ? 1 : -1))
                .map(item => {
                  const { entity_id, device_id, name, provider, sensor_type } =
                    item

                  return (
                    <MenuItem key={entity_id} value={entity_id}>
                      (ID:
                      {entity_id}) {provider} :: {device_id} :: {sensor_type} ::{' '}
                      {name}
                    </MenuItem>
                  )
                })}
            </KSelect>

            {sensor.parent_id &&
              !fSensors.find(f => Number(f.entity_id) === sensor.parent_id) && (
                <FormHelperText>
                  Der übergeordnete Sensor ist kein Sensor dieses Gebäudes
                </FormHelperText>
              )}

            <KSelect
              disabled={!sensor.is_active}
              error={
                sensor.successor_id &&
                !fSensors.find(f => Number(f.entity_id) === sensor.successor_id)
              }
              label={`Nachfolge Sensor (ID:
                        ${sensor.successor_id})`}
              onChange={onChangeSuccesorId}
              value={sensor.successor_id || ''}
            >
              <MenuItem disabled={!sensor.successor_id} value={null}>
                Übergeordneten Sensor wählen ...
              </MenuItem>
              {fSensors
                .filter(f => f.entity_id !== sensor.entity_id)
                .sort((a, b) => (a.device_id > b.device_id ? 1 : -1))
                .map(item => {
                  const { entity_id, device_id, name, provider, sensor_type } =
                    item

                  return (
                    <MenuItem key={entity_id} value={entity_id}>
                      (ID:
                      {entity_id}) {provider} :: {device_id} :: {sensor_type} ::{' '}
                      {name}
                    </MenuItem>
                  )
                })}
            </KSelect>

            <HStack justifyContent={'space-between'} sx={{ m: 1 }}>
              <AvatarGroup max={10}>
                {sensorUsers.map(id => {
                  const uRecord = uPool.find(f => Number(f.id) === id)
                  if (!uRecord) return <Box key={id} />

                  const { firstname, image_url, lastname } = uRecord.attributes
                  const title = `${firstname} ${lastname}`

                  return (
                    <Tooltip key={id} title={title}>
                      <Avatar src={image_url} />
                    </Tooltip>
                  )
                })}
              </AvatarGroup>

              <IconButton onClick={() => setShowAssignment(true)}>
                <SettingsTwoTone />
              </IconButton>
            </HStack>
          </Section>

          <Section title={'Zählerstand'}>
            <KTextField
              disabled={!sensor.is_active}
              label={'Zählernummer (meter_number)'}
              onChange={onChangeMeterNumber}
              value={sensor.meter_number || ''}
            />

            <HStack>
              <KTextField
                disabled={!sensor.is_active}
                label={'Zählerstand (counter_reading)'}
                onChange={onChangeCounterReading}
                value={germanNotation(sensor.counter_reading) || ''}
              />

              <KTextField
                disabled={!sensor.is_active}
                label={'Einheit (meter_unit)'}
                onChange={onChangeMeterUnit}
                value={sensor?.meter_unit ?? ''}
              />

              <KTextField
                disabled={!sensor.is_active}
                label={'Datum (counter_reading_date)'}
                onChange={onChangeCounterReadingDate}
                type={'date'}
                value={sensor.counter_reading_date}
              />
            </HStack>
          </Section>

          <Section title={'Temperaturbereinigung'}>
            <KSelect
              disabled={!sensor.is_active}
              label={'Temperaturbereinigung'}
              onChange={onChangeCorrectionType}
              value={correctionType}
            >
              {[
                { label: 'keine Angabe', value: 0 },
                { label: 'beides', value: 3 },
                { label: 'Heizgradtage', value: 1 },
                { label: 'Kühlgradstunden', value: 2 },
              ].map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </KSelect>
            <HStack>
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                {['outdoor', 'indoor'].map(sensorType => {
                  const sensorList =
                    sensorType === 'outdoor' ? outdoorSensor : indoorSensor

                  return (
                    <Box key={sensorType} sx={{ mb: 2 }}>
                      <ListSubheader>
                        {sensorType === 'outdoor'
                          ? 'Außentemperatur'
                          : 'Innentemperatur'}
                      </ListSubheader>

                      <HStack direction={'row'} spacing={1}>
                        <IconButton
                          onClick={() => {
                            setShowSensorAssignment(true)
                            setSelectedSensorType(sensorType as any)
                          }}
                          disabled={
                            correctionType === 0 ||
                            (correctionType === 2 && sensorType === 'indoor')
                          }
                        >
                          <SettingsTwoTone />
                        </IconButton>

                        {sensorList.map(entity_id => {
                          const fRecord = fSensors.find(
                            f => String(f.entity_id) === String(entity_id)
                          )
                          if (!fRecord) return <Box key={entity_id} />

                          return (
                            <Tooltip key={entity_id} title={fRecord.name}>
                              <Chip
                                avatar={<MemoryTwoTone />}
                                label={fRecord.name}
                              />
                            </Tooltip>
                          )
                        })}
                      </HStack>
                    </Box>
                  )
                })}
              </Box>
            </HStack>
          </Section>

          <Section title={'Batterie'}>
            <KTextField
              disabled={!sensor.is_active}
              label={'Kommentar (battery_comment)'}
              onChange={onChangeBatteryComment}
              value={germanNotation(sensor.battery_comment) || ''}
              variant={'outlined'}
            />

            <KTextField
              disabled={!sensor.is_active}
              label={'Datum (battery_date)'}
              onChange={onChangeBatteryDate}
              type={'date'}
              value={sensor.battery_date || ''}
            />

            <KTextField
              disabled={!sensor.is_active}
              label={'Typ (battery_type)'}
              onChange={onChangeBatteryType}
              value={sensor.battery_type || ''}
            />

            <HStack>
              <KTextField
                disabled={!sensor.is_active}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position={'start'}>
                      Erinnerung nach
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position={'start'}>Monaten</InputAdornment>
                  ),
                  inputProps: {
                    min: 3,
                    style: { textAlign: 'right' },
                  },
                }}
                label={'Warnung (warning_after_months)'}
                onChange={onChangeBatteryWarning}
                type={'number'}
                value={sensor.warning_after_months || ''}
              />

              <KTextField
                disabled={!sensor.is_active}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position={'start'}>
                      Erinnerung nach
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position={'start'}>Monaten</InputAdornment>
                  ),
                  inputProps: {
                    min: 3,
                    style: { textAlign: 'right' },
                  },
                }}
                label={'Alarm (alert_after_months)'}
                onChange={onChangeBatteryAlert}
                type={'number'}
                value={sensor.alert_after_months || ''}
              />
            </HStack>
          </Section>

          {attributes.length > 0 && (
            <Section title={`(Optionale) Attribute für SensorID ${sId}`}>
              {attributes.map((item: any) => {
                const { attribute_code, label, value } = item

                const onChange = event =>
                  handleChangeAttr(event, attribute_code)

                return (
                  <KTextField
                    disabled={!sensor.is_active}
                    key={label}
                    label={`${label} (${attribute_code})`}
                    onChange={onChange}
                    type={attribute_code === 'invoicing' ? 'date' : 'number'}
                    value={value}
                  />
                )
              })}
            </Section>
          )}
        </Collapse>
        {/* Einstellungen End */}
        {/* Payload */}
        <Collapse in={config.tab === 1}>
          <Section title={'Payload'}>
            <JsonView collapsed={false} src={sensorPayload} />
          </Section>
        </Collapse>
        {/* Payload */}
        {/* Aktivität */}
        <Collapse in={config.tab === 2}>
          <Section title={'Aktivität'}>
            <Chart
              chartLanguage={'de_DE'}
              chartType={'AreaChart'}
              loader={<Box>Loading Chart</Box>}
              data={series}
              options={{
                annotations: { stem: { color: '#097138' }, style: 'line' },
                chartArea: { height: '80%', width: '90%' },
                colors: ['#9ECE67'],
                curveType: 'function',
                legend: 'none',
                hAxis: { format: 'HH:mm' },
                vAxis: {
                  format: `#,###.## ${sensor.unit}`,
                  viewWindow: { min: rangeMin, max: rangeMax },
                },
              }}
            />

            <Divider sx={{ mt: 5, mb: 2 }} />

            <HStack justifyContent={'space-evenly'}>
              <KTextField
                label={
                  sensor
                    ? SensorType(sensor.sensor_type, sensor.type_in_folder)
                    : ''
                }
                readOnly
                unit={sensor?.unit}
                value={sensorData?.value ?? ''}
              />

              <KTextField
                label={'letzte Aktivität'}
                readOnly
                value={
                  sensorData
                    ? getRelativeDate(
                        String(new Date(Date.parse(sensorData.time)))
                      )
                    : ''
                }
              />
            </HStack>
          </Section>
        </Collapse>
        {/* Aktivität */}
        {/* Faktor Zeitraum */}
        <Collapse in={config.tab === 3}>
          <Section title={'Faktoren'}>
            {isConsumptionSensor ? (
              <SensorFactors sensor={sensor} contractId={Number(contractId)} />
            ) : (
              <Alert severity={'info'}>
                <AlertTitle>Funktion nicht verfügbar</AlertTitle>
                Dieser Sensor ist kein Verbrauchssensor und hat keine Faktoren
              </Alert>
            )}
          </Section>
        </Collapse>
        {/* Benchmarks */}
        <Collapse in={config.tab === 4}>
          <Section title={'Benchmark'}>
            {isConsumptionSensor ? (
              <Benchmarks sensor={sensor} contractId={Number(contractId)} />
            ) : (
              <Alert severity={'info'}>
                <AlertTitle>Funktion nicht verfügbar</AlertTitle>
                Dieser Sensor ist kein Verbrauchssensor und hat keine Benchmarks
              </Alert>
            )}
          </Section>
        </Collapse>
        {/* Benchmarks */}
        {/* Verbrauchsänderung*/}
        <Collapse in={config.tab === 5} sx={{ textAlign: 'center' }}>
          <>
            {attributesList.map(attr => (
              <KTextField
                key={attr.attribute_code}
                disabled={!sensor.is_active}
                label={attr.label}
                onChange={event => handleChangeAttr(event, attr.attribute_code)}
                type={'number'}
                value={attr.value}
                InputLabelProps={{ shrink: true }}
                sx={{ width: '100%' }}
              />
            ))}
          </>
        </Collapse>
        {/* Verbrauchsänderung*/}
        {/* Custom Field*/}
        <Collapse in={config.tab === 6}>
          {isConsumptionSensor ? (
            <CustomFields
              sensor={sensor}
              onSave={customFields => {
                setSensor({
                  ...sensor,
                  custom_fields: customFields,
                })
              }}
            />
          ) : (
            <Alert severity={'info'}>
              <AlertTitle>Funktion nicht verfügbar</AlertTitle>
              Dieser Sensor ist kein Verbrauchssensor und hat keine Custom
              Fields
            </Alert>
          )}
        </Collapse>
        {/* Custom Field*/}
      </KPage>
    </>
  )
}
